import { cornerFingerprint, rawTopologyFingerprint } from './fingerprints.js';
import { triangleRenderability } from './validation.js';

/**
 * Structural health of one editable mesh.
 *
 * TWO RULES SHAPE THIS WHOLE MODULE.
 *
 * 1. FINDINGS ARE SEPARATE FROM THE VERDICT. An open mesh is not a broken mesh —
 *    a plane, a shell, a cloth panel are all legitimately open. Boundary is
 *    always reported and only drives the verdict when the caller says the mesh
 *    is supposed to be closed, via `expectedClosed`. `verdictContributors` names
 *    exactly which findings drove the result, so a caller who disagrees with the
 *    policy can apply their own without re-deriving anything.
 *
 * 2. RAW TOPOLOGY FIRST, DERIVED LAYERS SECOND. `computeGeometricAdjacency`
 *    throws on a mesh containing a degenerate face, so anything computed through
 *    it is unavailable exactly when a diagnosis is most wanted. Every finding
 *    here is computed from raw topology, and the adjacency-derived corner count
 *    is reported as unavailable rather than allowed to fail the whole call.
 *
 * EDGES ARE POSITION-WELDED, NOT RAW. Canonical topology duplicates vertices
 * across hard edges, so a captured `BoxGeometry` has 24 raw edges with a single
 * adjacent face — it would read as riddled with holes while being a closed cube.
 * Measured: raw occurrence tally {1: 24, 2: 6}, position-welded {2: 18}. Welding
 * by exact position is what makes "is this closed" mean what a caller means by
 * it, and it is also why this module can diagnose meshes `orientFacesOutside`
 * refuses outright.
 */

const positionKey = (position) => position.join(',');

function facePositions(face, positionById) {
  return face.vertexIds.map((id) => positionById.get(id));
}

/** Undirected position-welded edges, with how many face corners run along each. */
function weldedEdges(topology, positionById) {
  const edges = new Map();
  for (const face of topology.faces) {
    const ids = face.vertexIds;
    for (let i = 0; i < ids.length; i++) {
      const a = positionKey(positionById.get(ids[i]));
      const b = positionKey(positionById.get(ids[(i + 1) % ids.length]));
      if (a === b) continue; // a collapsed edge is a degeneracy finding, not a boundary
      const key = a < b ? `${a}|${b}` : `${b}|${a}`;
      const entry = edges.get(key);
      if (entry) entry.count += 1;
      else edges.set(key, { count: 1, a, b, faceIds: [] });
      edges.get(key).faceIds.push(face.id);
    }
  }
  return edges;
}

/** Connected components over position-welded vertices. */
function componentsOf(topology, positionById, edges) {
  const parent = new Map();
  const find = (x) => {
    if (!parent.has(x)) parent.set(x, x);
    let root = x;
    while (parent.get(root) !== root) root = parent.get(root);
    while (parent.get(x) !== root) {
      const next = parent.get(x);
      parent.set(x, root);
      x = next;
    }
    return root;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  for (const face of topology.faces) {
    for (const id of face.vertexIds) find(positionKey(positionById.get(id)));
  }
  for (const { a, b } of edges.values()) union(a, b);

  const members = new Map();
  for (const key of parent.keys()) {
    const root = find(key);
    if (!members.has(root)) members.set(root, []);
    members.get(root).push(key);
  }
  return members;
}

/**
 * Signed volume via the divergence theorem, computed straight from positions.
 *
 * Deliberately not routed through `faceNormal` or `orientFacesOutside`: both
 * refuse a degenerate face, and a single sliver should not prevent reporting
 * that the rest of a component is inside out. A degenerate triangle contributes
 * exactly zero here, which is the right answer for a face with no area.
 */
function signedVolume(faces, positionById) {
  let total = 0;
  for (const face of faces) {
    const [a, b, c] = facePositions(face, positionById);
    total += (
      a[0] * (b[1] * c[2] - b[2] * c[1])
      - a[1] * (b[0] * c[2] - b[2] * c[0])
      + a[2] * (b[0] * c[1] - b[1] * c[0])
    ) / 6;
  }
  return total;
}

/** Longest edge over shortest edge: a plain, explainable sliver measure. */
function aspectRatio(positions) {
  const lengths = [0, 1, 2].map((i) => {
    const p = positions[i];
    const q = positions[(i + 1) % 3];
    return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
  });
  const shortest = Math.min(...lengths);
  return shortest === 0 ? Infinity : Math.max(...lengths) / shortest;
}

function percentile(sorted, fraction) {
  if (sorted.length === 0) return null;
  const index = Math.min(sorted.length - 1, Math.floor(fraction * sorted.length));
  return sorted[index];
}

/**
 * Describe the position-welded boundary, reporting only what is well defined.
 *
 * A boundary edge is one with a single adjacent face corner.
 *
 * WHY THERE IS NO "LOOP COUNT". A boundary component whose vertices all have
 * even degree of 4 or more admits several valid edge-disjoint cycle
 * decompositions with DIFFERENT cycle counts — two quads meeting at one vertex
 * decompose either as two 4-cycles or as one 8-edge Eulerian circuit. "How many
 * loops" is therefore a property of a chosen traversal, not of the mesh, and
 * any single number would be an arbitrary pick dressed up as a measurement.
 *
 * What IS well defined, and is what this reports:
 *   edgeCount        how much boundary there is at all
 *   components       connected components of the boundary graph, with the edge
 *                    count of each (edges, not vertices)
 *   simpleLoops      components in which every vertex has degree exactly 2, so
 *                    the component is unambiguously one closed loop
 *   branchVertices   boundary vertices of degree > 2 — where loops meet, and
 *                    exactly why a component may not be a single loop
 *   danglingVertices boundary vertices of degree 1 — an unclosed chain
 *
 * An earlier version counted connected components and called them loops, then
 * reported each component's VERTEX count as a loop "length". On the fixture
 * above that read as "1 loop of length 7" where the truth is 8 boundary edges
 * forming two cycles that meet at a degree-4 vertex.
 */
function boundaryStructure(edges) {
  const boundary = [...edges.values()].filter((edge) => edge.count === 1);

  const incident = new Map();
  for (const edge of boundary) {
    for (const vertex of [edge.a, edge.b]) {
      if (!incident.has(vertex)) incident.set(vertex, []);
      incident.get(vertex).push(edge);
    }
  }

  const seenEdges = new Set();
  const components = [];
  for (const start of [...incident.keys()].sort()) {
    if (incident.get(start).every((edge) => seenEdges.has(edge))) continue;
    const vertices = new Set([start]);
    let edgeCount = 0;
    const stack = [start];
    while (stack.length > 0) {
      const vertex = stack.pop();
      for (const edge of incident.get(vertex) ?? []) {
        if (seenEdges.has(edge)) continue;
        seenEdges.add(edge);
        edgeCount += 1;
        for (const next of [edge.a, edge.b]) {
          if (vertices.has(next)) continue;
          vertices.add(next);
          stack.push(next);
        }
      }
    }
    components.push({
      edgeCount,
      isSimpleLoop: [...vertices].every((vertex) => incident.get(vertex).length === 2),
    });
  }

  const degreeOf = (vertex) => incident.get(vertex).length;
  const branch = [...incident.keys()].filter((v) => degreeOf(v) > 2).sort();
  const dangling = [...incident.keys()].filter((v) => degreeOf(v) === 1).sort();

  return {
    boundary,
    edgeCount: boundary.length,
    components: {
      count: components.length,
      edgeCounts: components.map((c) => c.edgeCount).sort((x, y) => x - y),
    },
    simpleLoops: { count: components.filter((c) => c.isSimpleLoop).length },
    branchVertices: { positions: branch, maxDegree: branch.length === 0 ? 0 : Math.max(...branch.map(degreeOf)) },
    danglingVertices: { positions: dangling },
  };
}

/**
 * @param topology       canonical topology
 * @param expectedClosed true | false | null — null means boundary never drives
 *                       the verdict, which is the default and the safe one
 * @param sampleLimit    caps every sample array; counts stay exact
 */
export async function meshDiagnostics(topology, { expectedClosed = null, sampleLimit = 20 } = {}) {
  if (expectedClosed !== null && typeof expectedClosed !== 'boolean') {
    throw new Error('meshDiagnostics expectedClosed must be true, false or null');
  }
  if (!Number.isInteger(sampleLimit) || sampleLimit < 0) {
    throw new Error('meshDiagnostics sampleLimit must be an integer >= 0');
  }
  const cap = (values) => values.slice(0, sampleLimit);

  const positionById = new Map(topology.vertices.map((vertex) => [vertex.id, vertex.position]));

  // --- raw-topology findings, always computable ---------------------------
  const degenerate = [];
  const ratios = [];
  for (const face of topology.faces) {
    const positions = facePositions(face, positionById);
    const renderability = triangleRenderability(positions);
    if (!renderability.renderable) degenerate.push({ id: face.id, reason: renderability.reason });
    else ratios.push({ id: face.id, ratio: aspectRatio(positions) });
  }

  const edges = weldedEdges(topology, positionById);
  const nonManifold = [...edges.values()].filter((edge) => edge.count > 2);
  const boundaryInfo = boundaryStructure(edges);

  const referenced = new Set(topology.faces.flatMap((face) => face.vertexIds));
  const orphans = topology.vertices.filter((vertex) => !referenced.has(vertex.id)).map((vertex) => vertex.id);

  const componentMembers = componentsOf(topology, positionById, edges);
  const componentOfKey = new Map();
  [...componentMembers.entries()].forEach(([root, keys], index) => {
    for (const key of keys) componentOfKey.set(key, index);
  });
  const facesByComponent = new Map();
  for (const face of topology.faces) {
    const index = componentOfKey.get(positionKey(positionById.get(face.vertexIds[0])));
    if (!facesByComponent.has(index)) facesByComponent.set(index, []);
    facesByComponent.get(index).push(face);
  }
  // Only a CLOSED component has a meaningful volume sign; an open one is
  // reported as unknown rather than guessed at.
  const openComponents = new Set();
  for (const { a, b } of boundaryInfo.boundary) {
    openComponents.add(componentOfKey.get(a));
    openComponents.add(componentOfKey.get(b));
  }
  const inverted = [];
  for (const [index, faces] of facesByComponent) {
    if (openComponents.has(index)) continue;
    if (signedVolume(faces, positionById) < 0) inverted.push(index);
  }

  const sortedRatios = ratios.map((entry) => entry.ratio).sort((x, y) => x - y);
  const worst = [...ratios].sort((x, y) => y.ratio - x.ratio);

  // --- derived layer, allowed to be unavailable ---------------------------
  const corner = await cornerFingerprint(topology);
  const adjacencyAvailable = corner.value !== null;

  const findings = {
    degenerateFaces: {
      count: degenerate.length,
      sampleIds: cap(degenerate.map((entry) => entry.id)),
      reasons: [...new Set(degenerate.map((entry) => entry.reason))].sort(),
    },
    nonManifoldEdges: {
      count: nonManifold.length,
      sampleFaceIds: cap([...new Set(nonManifold.flatMap((edge) => edge.faceIds))].sort()),
    },
    boundary: {
      edgeCount: boundaryInfo.edgeCount,
      components: boundaryInfo.components,
      simpleLoops: boundaryInfo.simpleLoops,
      branchVertices: {
        count: boundaryInfo.branchVertices.positions.length,
        maxDegree: boundaryInfo.branchVertices.maxDegree,
        samplePositions: cap(boundaryInfo.branchVertices.positions),
      },
      danglingVertices: {
        count: boundaryInfo.danglingVertices.positions.length,
        samplePositions: cap(boundaryInfo.danglingVertices.positions),
      },
    },
    components: { count: facesByComponent.size },
    invertedComponents: {
      count: inverted.length,
      componentIndices: cap(inverted),
      undetermined: openComponents.size > 0,
    },
    orphanVertices: { count: orphans.length, sampleIds: cap(orphans) },
    aspectRatio: {
      p50: percentile(sortedRatios, 0.5),
      p90: percentile(sortedRatios, 0.9),
      max: sortedRatios.length === 0 ? null : sortedRatios[sortedRatios.length - 1],
      worstFaceIds: cap(worst.map((entry) => entry.id)),
    },
  };

  // --- verdict ------------------------------------------------------------
  // Only structural breakage counts by default. Boundary is policy-driven;
  // orphan vertices and sliver ratios are reported, never fatal — they are
  // things a caller may want to know, not things that make a mesh invalid.
  const verdictContributors = [];
  if (findings.degenerateFaces.count > 0) verdictContributors.push('degenerateFaces');
  if (findings.nonManifoldEdges.count > 0) verdictContributors.push('nonManifoldEdges');
  if (findings.invertedComponents.count > 0) verdictContributors.push('invertedComponents');
  if (expectedClosed === true && findings.boundary.edgeCount > 0) verdictContributors.push('boundary');
  if (expectedClosed === false && findings.boundary.edgeCount === 0) verdictContributors.push('boundary');

  return {
    verdict: verdictContributors.length === 0 ? 'clean' : 'unhealthy',
    verdictContributors,
    expectedClosed,
    findings,
    adjacencyAvailable,
    adjacencyError: corner.error,
    vertexCount: topology.vertices.length,
    faceCount: topology.faces.length,
    rawTopologyFingerprint: await rawTopologyFingerprint(topology),
    cornerFingerprint: corner.value,
  };
}
